import React, { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "react-toastify";

const BlogStatusBox2 = ({ blogId = null, initialData = {}, onSuccess, isEdit, className = "" }) => {
  const [status, setStatus] = useState("Publish");
  const [visibility, setVisibility] = useState("Public");
  const [password, setPassword] = useState("");

  const [showEdit, setShowEdit] = useState(false);
  const [showVisEdit, setShowVisEdit] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const [tempStatus, setTempStatus] = useState("Publish");
  const [tempVisibility, setTempVisibility] = useState("Public");

  useEffect(() => {
    if (initialData?.status) {
      setStatus(initialData.status);
      setTempStatus(initialData.status);
    } else {
      setTempStatus(status);
    }

    if (initialData?.visibility) {
      setVisibility(initialData.visibility);
      setTempVisibility(initialData.visibility);
      setShowPasswordInput(initialData.visibility === "Password");
    }

    if (initialData?.password) setPassword(initialData.password);
  }, [initialData]);

  const handleStatusOk = () => {
    setStatus(tempStatus);
    setShowEdit(false);
  };

  const handleStatusCancel = () => {
    setTempStatus(status);
    setShowEdit(false);
  };

  const handleVisibilitySelect = (value) => {
    setTempVisibility(value);
    setShowPasswordInput(value === "Password");
  };

  const handleVisibilityOk = () => {
    setVisibility(tempVisibility);
    if (tempVisibility !== "Password") setPassword("");
    setShowVisEdit(false);
  };

  const handleVisibilityCancel = () => {
    setTempVisibility(visibility);
    setShowPasswordInput(visibility === "Password");
    if (visibility !== "Password") setPassword("");
    setShowVisEdit(false);
  };

  const saveBlog = async (actionType) => {
    try {
      setLoading(true);

      let finalStatus = tempStatus;
      if (actionType === "draft") finalStatus = "Draft";
      if (actionType === "publish") finalStatus = "Publish";
      if (actionType === "preview") finalStatus = "Draft";

      if (actionType === "draft" || actionType === "publish" || actionType === "preview") {
        setStatus(finalStatus);
        setTempStatus(finalStatus);
      }

      const finalPayload = {
        status: finalStatus,
        visibility,
        password: visibility === "Password" ? password : "",
        actionType,
      };

      if (onSuccess) {
        await onSuccess(finalPayload);
      }
    } catch (err) {
      toast.error("Failed to save blog");
    } finally {
      setLoading(false);
    }
  };

  const handleAction = (actionType) => {
    if (actionType === "preview") {
      saveBlog(actionType);
      return;
    }
    saveBlog(actionType);
  };

  return (
    <div className={`add-blog-status-box ${className || ""}`}>
      <h3>Blog Status</h3>

      <div className="add-blog-status-actions">
        <button
          type="button"
          disabled={loading}
          className="add-blog-status-btn add-blog-status-btn--ghost"
          onClick={() => handleAction("preview")}
        >
          Preview
        </button>

        <button
          type="button"
          disabled={loading}
          className="add-blog-status-btn add-blog-status-btn--draft"
          onClick={() => handleAction("draft")}
        >
          {loading ? "Saving..." : "Save as Draft"}
        </button>
      </div>

      <div className="add-blog-status-meta">
        <p>
          Status: <strong>{status}</strong>
          <span className="add-blog-status-link" onClick={() => setShowEdit(true)} role="button" tabIndex={0}>
            edit
          </span>
        </p>

        {showEdit && (
          <div className="mt-2">
            <select
              value={tempStatus}
              onChange={(e) => setTempStatus(e.target.value)}
              className="add-blog-status-select"
            >
              <option value="Publish">Publish</option>
              <option value="Draft">Draft</option>
            </select>

            <div className="add-blog-status-inline-actions">
              <button type="button" className="add-blog-status-ok" onClick={handleStatusOk}>
                OK
              </button>
              <span className="add-blog-status-cancel" onClick={handleStatusCancel} role="button" tabIndex={0}>
                Cancel
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="add-blog-status-meta">
        <p>
          Visibility: <strong>{visibility}</strong>
          <span className="add-blog-status-link" onClick={() => setShowVisEdit(true)} role="button" tabIndex={0}>
            edit
          </span>
        </p>

        {showVisEdit && (
          <div className="mt-2 space-y-3">
            <select
              value={tempVisibility}
              onChange={(e) => handleVisibilitySelect(e.target.value)}
              className="add-blog-status-select"
            >
              <option value="Public">Public</option>
              <option value="Private">Private</option>
              <option value="Password">Password</option>
            </select>

            {showPasswordInput && (
              <div>
                <span className="add-blog-form-label">Password</span>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="add-blog-status-input"
                    placeholder="Enter password"
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-2 text-[var(--text-muted)]"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            )}

            <div className="add-blog-status-inline-actions">
              <button type="button" className="add-blog-status-ok" onClick={handleVisibilityOk}>
                OK
              </button>
              <span className="add-blog-status-cancel" onClick={handleVisibilityCancel} role="button" tabIndex={0}>
                Cancel
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="add-blog-status-footer">
        <button
          type="button"
          disabled={loading}
          className="add-blog-status-btn add-blog-status-btn--primary w-full sm:w-auto"
          onClick={() => handleAction("publish")}
        >
          {loading ? "Publishing..." : isEdit ? "Update" : "Publish"}
        </button>
      </div>
    </div>
  );
};

export default BlogStatusBox2;
